/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useRef} from 'react'
import {useParams, useHistory} from 'react-router'
import {
    Stack,
    Box,
    SimpleGrid,
    Spinner
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'

import {useOrderDetail} from '../../hooks/use-order-detail'
import {DSSActions} from './partials/dss-actions'
import {AbsoluteCenter, useBreakpointValue} from '@chakra-ui/react'
import {
    OrderProducts,
    OrderShippingAddress,
    OrderPayment,
    ManageOrder,
    OrderDetailsSummary,
    OrderInfo,
    OrderProductStatus
} from './partials'
import OrderBarCode from './order-bar-code'
const onClient = typeof window !== 'undefined'

// CONSTANTS
import {ORDER_RAW_STATUS, ORDER_PRODUCTS_RENDER_MODES, ORDER_RETURN_RAW_STATUS} from './constant'
import {isCanceledOrderStatus} from '../../utils/utils'
import AccountBreadCrumb from './account-breadcrumb'

const DSS_ACTION_STATES = { PICKED_UP: 'Picked Up' }

const AccountOrderDetail = () => {
    const {orderId} = useParams()
    const {currency} = useCurrency()
    const isMobile = useBreakpointValue({base: true, md: false})

    const {data: {order = {}} = {}, isLoading: isOrderLoading} = useOrderDetail(orderId, {
        enabled: onClient && !!orderId
    })

    const history = useHistory()
    useEffect(() => {
        if (isOrderLoading) return

        if (!order || !order.orderNo) {
            history.replace('/account/orders')
        }
    }, [order, isOrderLoading, history])

    const headingRef = useRef()
    useEffect(() => {
        // Focus the 'Order Details' header when the component mounts for accessibility
        headingRef?.current?.focus()
    }, [])

    const getOrderProductsMode = (order) =>
        ORDER_RAW_STATUS.TRACKING.includes(order.statusRaw?.toLowerCase()) &&
        order.packages?.length > 0
            ? ORDER_PRODUCTS_RENDER_MODES.PACKAGES
            : ORDER_PRODUCTS_RENDER_MODES.ITEMS

    const mode = getOrderProductsMode(order)
    const orderProductsData = mode === ORDER_PRODUCTS_RENDER_MODES.PACKAGES ? order : order.items
    const hasCanceledItem = order?.items?.some((item) => isCanceledOrderStatus(item?.statusRaw))
    const shouldShowOrderProductStatus =
        isCanceledOrderStatus(order?.statusRaw) ||
        hasCanceledItem ||
        Object.values(ORDER_RETURN_RAW_STATUS).includes(order?.statusRaw?.toLowerCase())

    if (isOrderLoading) {
        return (
            // eslint-disable-next-line prettier/prettier
            <AbsoluteCenter>
                <Spinner />
            </AbsoluteCenter>
        )
    }

    return (
        <Stack spacing={6} data-testid="account-order-details-page">
            {isMobile && <AccountBreadCrumb params={{orderId, orderNo: order?.orderNo}} />}
            <SimpleGrid columns={{base: 1, lg: 1}} spacing={{base: 6, lg: '120px'}}>
                <Box>
                    <Stack
                        w={{base: 'full', lg: '458px', '2xl': '458px'}}
                        display="flex"
                        flexDirection="column"
                        gap={
                            order?.orderStatus === DSS_ACTION_STATES.PICKED_UP
                                ? {base: '3px', lg: '6px'}
                                : {base: '12px', lg: '24px'}
                        }
                    >
                        <OrderInfo order={order} headingRef={headingRef} />
                        {order?.isOnlineOrder && <DSSActions order={order} />}
                        {shouldShowOrderProductStatus && (
                            <OrderProductStatus
                                orderBrand={order?.orderBrand}
                                orderProductsData={orderProductsData}
                                currency={currency}
                                mode={mode}
                                shippingMethod={order?.shippingMethod}
                                shippingFee={order?.shippingFee}
                                isOnlineOrder={order?.isOnlineOrder}
                                orderStatusRaw={order?.statusRaw}
                            />
                        )}
                        <Box display={{base: 'block', lg: 'none'}}>
                            <ManageOrder
                                containerProps={{mt: 2}}
                                dividerProps={{mb: 0}}
                                {...order.returnableStatus}
                                returnLabel={order.returnLabel}
                            />
                        </Box>
                        {order?.isOnlineOrder && <OrderShippingAddress order={order} />}
                        <OrderProducts
                            orderBrand={order.orderBrand}
                            orderProductsData={orderProductsData}
                            shippingMethod={order.shippingMethod}
                            bopisStoreNumber={order.bopisStoreNumber}
                            shippingFee={order.shippingFee}
                            shippingMethodDetails={order.shippingMethodDetails}
                            storeInfo={order.storeInfo}
                            currency={currency}
                            mode={mode}
                            isOnlineOrder={order?.isOnlineOrder}
                        />
                        <OrderPayment order={order} />
                        <Box maxW={{base: 'full', lg: '100%'}}>
                            <Box display={{base: 'none', lg: 'block'}}>
                                <ManageOrder
                                    {...order.returnableStatus}
                                    returnLabel={order.returnLabel}
                                />
                            </Box>
                            <OrderDetailsSummary {...order} currency={currency} />
                            <OrderBarCode
                                orderNo={order.orderNo}
                                hasOnlyGiftCards={order.hasonlyGiftCards}
                                isOnlineOrder={order?.isOnlineOrder}
                            />
                        </Box>
                    </Stack>
                </Box>
            </SimpleGrid>
        </Stack>
    )
}

AccountOrderDetail.getTemplateName = () => 'account-order-history'

export default AccountOrderDetail
